import { useState, useCallback } from 'react'
import { usePlayground } from '../store'
import { SourceView } from './SourceView'
import { ImportModal } from './ImportModal'
import { refactorRename } from '../api/enApi'
import { MobileSheet } from './MobileSheet'
import { TabToolbar, ToolbarBtn, ProcessingDot, Icons } from './TabToolbar'
import type { ParsedStatement } from '../api/types'
import styles from './StructurePanel.module.css'

export function StructurePanel() {
    const source = usePlayground((s) => s.source)
    const setSource = usePlayground((s) => s.setSource)
    const isProcessing = usePlayground((s) => s.isProcessing)
    const result = usePlayground((s) => s.result)
    const isMobile = usePlayground((s) => s.isMobile)
    const scene = usePlayground((s) => s.scene)
    const selectNode = usePlayground((s) => s.selectNode)
    const setActiveTab = usePlayground((s) => s.setActiveTab)
    const requestFocusNode = usePlayground((s) => s.requestFocusNode)
    const leftOpen = usePlayground((s) => s.leftOpen)
    const setLeftOpen = usePlayground((s) => s.setLeftOpen)

    const statements = result?.statements ?? []
    const [editingStmt, setEditingStmt] = useState<ParsedStatement | null>(null)
    const [showImportModal, setShowImportModal] = useState(false)
    const [mobileView, setMobileView] = useState<'cards' | 'editor'>('editor')
    const [copied, setCopied] = useState(false)

    const handleCopy = useCallback(async () => {
        try { await navigator.clipboard.writeText(source); setCopied(true); setTimeout(() => setCopied(false), 2000) } catch {}
    }, [source])
    const handleClear = useCallback(() => {
        if (confirm('Clear all content?')) setSource('')
    }, [setSource])
    const [addingNew, setAddingNew] = useState(false)

    const showToolbar = true
    const showContent = true

    return (
        <div className={styles.panel}>
            {showToolbar && <TabToolbar
                left={<>
                    <div className="w-8 h-8 flex items-center justify-center" style={{ color: 'var(--text-muted)' }}>{Icons.tabSource}</div>
                    <span className="text-xs font-bold text-text-muted font-mono uppercase tracking-widest">Source</span>
                    {isProcessing && <ProcessingDot />}
                </>}
                right={<>
                    <ToolbarBtn onClick={() => setShowImportModal(true)} title="Import">{Icons.import}</ToolbarBtn>
                    {!isMobile && <ToolbarBtn onClick={() => setLeftOpen(false)} title="Close panel">{Icons.close}</ToolbarBtn>}
                </>}
            />}

            {showContent && (isMobile || leftOpen) && <>
            {/* Editor/Cards toggle + Copy/Paste/Clear — same on desktop and mobile */}
            <div className="flex items-center px-2 py-1 shrink-0" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <button onClick={() => setMobileView('editor')} className="text-xs font-medium font-mono transition-colors active:scale-[0.97] h-8 px-2" style={{ color: mobileView === 'editor' ? 'var(--text)' : 'var(--text-muted)', borderBottom: mobileView === 'editor' ? '2px solid var(--accent)' : '2px solid transparent' }}>Editor</button>
                <button onClick={() => setMobileView('cards')} className="text-xs font-medium font-mono transition-colors active:scale-[0.97] h-8 px-2" style={{ color: mobileView === 'cards' ? 'var(--text)' : 'var(--text-muted)', borderBottom: mobileView === 'cards' ? '2px solid var(--accent)' : '2px solid transparent' }}>Cards</button>
                <div className="flex-1" />
                {mobileView === 'editor' && <>
                    <button onClick={handleCopy} className="text-xs font-mono transition-colors active:scale-[0.97] h-8 px-2" style={{ color: copied ? 'var(--accent)' : 'var(--text-muted)' }}>{copied ? 'Copied' : 'Copy'}</button>
                    <button onClick={handleClear} className="text-xs font-mono transition-colors active:scale-[0.97] h-8 px-2" style={{ color: 'var(--text-muted)' }}>Clear</button>
                </>}
                {mobileView === 'cards' && (
                    <button onClick={() => setAddingNew(true)} className="text-xs font-mono transition-colors active:scale-[0.97] h-8 px-2" style={{ color: 'var(--accent)' }}>+ Add action</button>
                )}
            </div>

            {/* Content area — same for both */}
            <div className={styles.editorArea}>
                {mobileView === 'editor' && (
                    <SourceView source={source} onChange={setSource} />
                )}
                {mobileView === 'cards' ? (
                        <div className="flex-1 overflow-y-auto">
                            {statements.length === 0 ? (
                                <div className="p-4 text-xs font-mono" style={{ color: 'var(--text-muted)' }}>
                                    {isProcessing ? 'Processing...' : 'No statements yet. Switch to editor to write EN code.'}
                                </div>
                            ) : (
                                <div className="p-3 space-y-2">
                                    {statements.map((stmt, i) => (
                                        <button
                                            key={i}
                                            onClick={() => {
                                                // Find the scene node matching this statement
                                                const node = scene?.nodes.find(n => {
                                                    if (n.displayName.toLowerCase() !== stmt.action.toLowerCase()) return false
                                                    if (n.actorLabel && stmt.subjects.length > 0) {
                                                        return stmt.subjects.some(s => s.toLowerCase() === n.actorLabel!.toLowerCase())
                                                    }
                                                    return true
                                                })
                                                if (node) {
                                                    selectNode(node.id)
                                                    requestFocusNode(node.id)
                                                    if (isMobile) setActiveTab('graph')
                                                }
                                            }}
                                            className="w-full text-left border active:scale-[0.97] transition-transform"
                                            style={{ borderColor: 'var(--border-subtle)', background: 'var(--surface-elevated)', borderRadius: 16, padding: '12px 16px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                                <div style={{ minWidth: 0, flex: 1 }}>
                                                    {stmt.subjects.length > 0 && (
                                                        <div style={{ fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--role-action)', marginBottom: 2 }}>
                                                            {stmt.subjects.join(', ').toLowerCase()}
                                                        </div>
                                                    )}
                                                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', lineHeight: 1.2, fontFamily: 'var(--font-sans)', letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
                                                        {stmt.action}
                                                    </div>
                                                    <div style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', marginTop: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                                                        {stmt.needs.length > 0 && <span>needs: {stmt.needs.join(', ')}</span>}
                                                        {stmt.yields.length > 0 && <span>yields: {stmt.yields.join(', ')}</span>}
                                                    </div>
                                                </div>
                                                {stmt.location && (
                                                    <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--role-locus)', background: 'color-mix(in srgb, var(--role-locus) 15%, transparent)', padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                        {stmt.location}
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 6 }}>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setEditingStmt(stmt) }}
                                                    className="active:scale-[0.97]"
                                                    style={{ fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--accent)', padding: '2px 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        if (!stmt.sourceRange || !confirm('Delete this statement?')) return
                                                        const lines = source.split('\n')
                                                        const before = lines.slice(0, stmt.sourceRange.startLine - 1)
                                                        const after = lines.slice(stmt.sourceRange.endLine)
                                                        while (after.length > 0 && after[0].trim() === '') after.shift()
                                                        setSource([...before, ...after].join('\n'))
                                                    }}
                                                    className="active:scale-[0.97]"
                                                    style={{ fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--danger)', padding: '2px 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : null}
            </div>


            {editingStmt && (
                <CardEditSheet stmt={editingStmt} source={source} setSource={setSource} onClose={() => setEditingStmt(null)} />
            )}
            {addingNew && (
                <AddActionSheet source={source} setSource={setSource} onClose={() => setAddingNew(false)} />
            )}

            {showImportModal && (
                <ImportModal
                    onImport={(text) => {
                        setSource(text)
                        setShowImportModal(false)
                    }}
                    onClose={() => setShowImportModal(false)}
                />
            )}
            </>}
        </div>
    )
}

/** Bottom sheet for editing a parsed statement — fields editable via refactor API */
function CardEditSheet({ stmt, source, setSource, onClose }: {
    stmt: ParsedStatement; source: string; setSource: (s: string) => void; onClose: () => void
}) {
    const [subjects, setSubjects] = useState(stmt.subjects.join(', '))
    const [action, setAction] = useState(stmt.action)
    const [needs, setNeeds] = useState(stmt.needs.join(', '))
    const [yields, setYields] = useState(stmt.yields.join(', '))
    const [location, setLocation] = useState(stmt.location ?? '')

    // Build the statement block from current field values
    const buildBlock = () => {
        const sub = subjects.trim()
        const act = action.trim()
        const n = needs.trim()
        const y = yields.trim()
        const loc = location.trim()
        if (!sub || !act || !n || !y) return null
        const lines = [sub, `do: ${act}`, `needs: ${n}`, `yields: ${y}`]
        if (loc) lines.push(`at: ${loc}`)
        return lines.join('\n')
    }

    // Save locally — replace only this statement's lines in the source
    const handleSaveLocal = () => {
        const block = buildBlock()
        if (!block) return
        const range = stmt.sourceRange
        if (!range) { onClose(); return }
        const lines = source.split('\n')
        const before = lines.slice(0, range.startLine - 1)
        const after = lines.slice(range.endLine)
        setSource([...before, block, ...after].join('\n'))
        onClose()
    }

    // Save & refactor — rename changed names globally across entire source
    const handleSaveRefactor = async () => {
        let s = source
        const rename = async (old: string, newVal: string) => {
            const r = await refactorRename(s, old, newVal)
            if (r.success && r.source) s = r.source
        }
        const newSubjects = subjects.split(',').map(x => x.trim()).filter(Boolean)
        for (let i = 0; i < stmt.subjects.length; i++) {
            if (newSubjects[i] && newSubjects[i] !== stmt.subjects[i]) await rename(stmt.subjects[i], newSubjects[i])
        }
        if (action.trim() && action.trim() !== stmt.action) await rename(stmt.action, action.trim())
        const newNeeds = needs.split(',').map(n => n.trim()).filter(Boolean)
        for (let i = 0; i < stmt.needs.length; i++) {
            if (newNeeds[i] && newNeeds[i] !== stmt.needs[i]) await rename(stmt.needs[i], newNeeds[i])
        }
        const newYields = yields.split(',').map(y => y.trim()).filter(Boolean)
        for (let i = 0; i < stmt.yields.length; i++) {
            if (newYields[i] && newYields[i] !== stmt.yields[i]) await rename(stmt.yields[i], newYields[i])
        }
        if (stmt.location && location.trim() && location.trim() !== stmt.location) await rename(stmt.location, location.trim())
        if (s !== source) setSource(s)
        onClose()
    }

    return (
        <MobileSheet open={true} onClose={onClose} title="Edit Statement">
            <div className="flex flex-col gap-4">
                <Field label="Subject(s)" value={subjects} onChange={setSubjects} placeholder="actor1, actor2" />
                <Field label="do:" value={action} onChange={setAction} placeholder="action name" />
                <Field label="needs:" value={needs} onChange={setNeeds} placeholder="input1, input2" />
                <Field label="yields:" value={yields} onChange={setYields} placeholder="output1, output2" />
                <Field label="at:" value={location} onChange={setLocation} placeholder="location (optional)" />
                <div className="flex gap-2">
                    <button
                        onClick={handleSaveLocal}
                        className="flex-1 py-2.5 rounded-xl text-xs font-bold font-mono tracking-wider transition-colors active:scale-[0.97]"
                        style={{ background: 'color-mix(in srgb, var(--accent) 20%, transparent)', color: 'var(--accent)' }}
                    >
                        Save
                    </button>
                    <button
                        onClick={handleSaveRefactor}
                        className="flex-1 py-2.5 rounded-xl text-xs font-bold font-mono tracking-wider transition-colors active:scale-[0.97]"
                        style={{ background: 'color-mix(in srgb, var(--accent) 10%, transparent)', color: 'var(--text-muted)' }}
                    >
                        Rename everywhere
                    </button>
                </div>
            </div>
        </MobileSheet>
    )
}

function Field({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder: string }) {
    return (
        <div>
            <label className="block text-2xs font-bold font-mono uppercase tracking-widest mb-1.5" style={{ color: 'var(--accent)' }}>{label}</label>
            <input
                type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
                className="w-full px-3 py-2.5 rounded-xl text-sm font-mono bg-transparent border outline-none transition-colors focus:border-accent"
                style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
            />
        </div>
    )
}

/** Bottom sheet for adding a new statement */
function AddActionSheet({ source, setSource, onClose }: { source: string; setSource: (s: string) => void; onClose: () => void }) {
    const [subjects, setSubjects] = useState('')
    const [action, setAction] = useState('')
    const [needs, setNeeds] = useState('')
    const [yields, setYields] = useState('')
    const [location, setLocation] = useState('')

    const handleSave = () => {
        const sub = subjects.trim()
        const act = action.trim()
        const n = needs.trim()
        const y = yields.trim()
        const loc = location.trim()

        if (!sub || !act || !n || !y) return // all required

        const lines = [sub, `do: ${act}`, `needs: ${n}`, `yields: ${y}`]
        if (loc) lines.push(`at: ${loc}`)

        const block = lines.join('\n')
        setSource(source.trimEnd() + '\n\n' + block)
        onClose()
    }

    return (
        <MobileSheet open={true} onClose={onClose} title="Add Statement">
            <div className="flex flex-col gap-4">
                <Field label="Subject(s)" value={subjects} onChange={setSubjects} placeholder="actor1, actor2" />
                <Field label="do:" value={action} onChange={setAction} placeholder="action name" />
                <Field label="needs:" value={needs} onChange={setNeeds} placeholder="input1, input2" />
                <Field label="yields:" value={yields} onChange={setYields} placeholder="output1, output2" />
                <Field label="at:" value={location} onChange={setLocation} placeholder="location (optional)" />
                <button
                    onClick={handleSave}
                    className="w-full py-2.5 rounded-xl text-xs font-bold font-mono tracking-wider transition-colors active:scale-[0.97]"
                    style={{ background: 'color-mix(in srgb, var(--accent) 20%, transparent)', color: 'var(--accent)' }}
                >
                    Add
                </button>
            </div>
        </MobileSheet>
    )
}
